import _ from 'lodash';
import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { getCreateEvent } from './queries';
import {
  AllowedActionConfig,
  DomainType,
  EntityConfig,
  WorkflowType,
} from './types';

type RevyreState = {
  entityId: string;
  workflowType: WorkflowType;
  domainType: DomainType;
  modules: Record<string, EntityConfig>;
  ddp: {
    currentJobId: string;
    jobDetails: Record<string, any>;
    isJobLoading: boolean;
  };
  list: {
    results: Record<string, any>[];
    columns: any;
    filters: [];
    metaData: {
      currentPage: number;
      maxPages: number;
      pageSize: number;
      totalCount: number;
    };
  };
  referredEntityAllowedActions: AllowedActionConfig[];
  uiState: {
    isDialogOpen: boolean;
    notification: {
      message: string;
      id?: string;
      variant?:
        | 'success'
        | 'error'
        | 'warning'
        | 'info'
        | 'default'
        | undefined;
    };
    loaderCount: number;
    tableParams: {
      filters?: Record<string, any>;
      sortBy?: string;
      sortType?: string;
    };
  };
};

const initialState: RevyreState = {
  entityId: '',
  // @ts-ignore
  workflowType: '' as WorkflowType,
  // @ts-ignore
  domainType: '' as DomainType,
  modules: {},
  ddp: {
    currentJobId: '',
    jobDetails: {},
    isJobLoading: true,
  },
  list: {
    results: [],
    columns: [],
    filters: [],
    metaData: {
      currentPage: 1,
      maxPages: 1,
      pageSize: 10,
      totalCount: 0,
    },
  },
  referredEntityAllowedActions: [],
  uiState: {
    isDialogOpen: false,
    notification: { message: '', id: '' },
    loaderCount: 0,
    tableParams: {},
  },
};

export const revyreSlice = createSlice({
  name: 'revyre',
  initialState,
  reducers: {
    setLoading(
      state: RevyreState,
      action: PayloadAction<{ loading: boolean }>,
    ) {
      if (action.payload.loading) {
        state.uiState.loaderCount += 1;
      } else if (state.uiState.loaderCount > 0) {
        state.uiState.loaderCount -= 1;
      }
    },
    setError(
      state: RevyreState,
      action: PayloadAction<{ message: string; id?: string }>,
    ) {
      state.uiState.notification = { ...action.payload, variant: 'error' };
    },
    setNotification(
      state: RevyreState,
      action: PayloadAction<{
        message: string;
        id?: string;
        variant?: 'success' | 'error' | 'warning' | 'info' | 'default';
      }>,
    ) {
      state.uiState.notification = action.payload;
    },
    setCurrentWorkflowType(
      state: RevyreState,
      action: PayloadAction<WorkflowType>,
    ) {
      state.workflowType = action.payload;
    },
    setDomainType(state: RevyreState, action: PayloadAction<DomainType>) {
      state.domainType = action.payload;
    },
    setModuleConfigData(
      state: RevyreState,
      action: PayloadAction<{
        domainType: DomainType;
        workflowType: WorkflowType;
        data;
      }>,
    ) {
      const { domainType, workflowType, data } = action.payload;
      // @ts-ignore
      state.modules[domainType !== 'workflow' ? domainType : workflowType] = {
        ...data,
        events: [
          getCreateEvent(workflowType, data?.createConfig),
          ...(data?.events || []),
        ],
      };
    },
    setModuleFeatureConfigData(
      state: RevyreState,
      action: PayloadAction<{ workflowType: string; data }>,
    ) {
      state.modules[action.payload.workflowType] = {
        ...(state.modules[action.payload.workflowType] || {}),
        events: [
          {
            allowedAction: 'create',
            type: `states.events.payloads/com.vymo.tech.${
              action.payload.workflowType
            }.model.${_.capitalize(action.payload.workflowType)}.json`,
            config: action.payload?.data?.payload,
          },
          ...(state.modules[action.payload.workflowType]?.events || []),
        ],
      };
    },
    setEntityListMetadata(
      state: RevyreState,
      action: PayloadAction<{
        currentPage?: number;
        pageSize?: number;
        maxPages?: number;
      }>,
    ) {
      state.list.metaData = { ...state.list.metaData, ...action.payload };
    },

    setCurrentBulkDownloadJobId(
      state: RevyreState,
      action: PayloadAction<string>,
    ) {
      state.ddp.currentJobId = action.payload;
    },

    setBulkDownloadStatus(state: RevyreState, action: PayloadAction<boolean>) {
      state.ddp.isJobLoading = action.payload;
    },

    setBulkDownloadJobData(
      state: RevyreState,
      action: PayloadAction<{ jobId: string; jobDetails: any }>,
    ) {
      const { jobId, jobDetails } = action.payload;

      state.ddp.jobDetails[jobId] = jobDetails;
    },

    setEntityListData(state: RevyreState, action: PayloadAction<{ data }>) {
      const { result, config, maxPages, totalCount } =
        action.payload?.data?.payload ?? {};
      state.list.results = result;
      state.list.columns = config?.columns;
      state.list.filters = config?.filters;
      state.list.metaData = {
        ...state.list.metaData,
        maxPages,
        totalCount,
      };
    },
    setReferredEntityAllowedActions(
      state: RevyreState,
      action: PayloadAction<Array<AllowedActionConfig>>,
    ) {
      state.referredEntityAllowedActions = action.payload;
    },
    resetEntityListData(state: RevyreState) {
      state.list = initialState.list;
      state.uiState.tableParams = initialState.uiState.tableParams;
    },
    setIsDialogOpen(state: RevyreState, action: PayloadAction<boolean>) {
      state.uiState.isDialogOpen = action.payload;
    },
  },
});

export const {
  setLoading,
  setError,
  setDomainType,
  setCurrentWorkflowType,
  setModuleConfigData,
  setModuleFeatureConfigData,
  setEntityListData,
  resetEntityListData,
  setIsDialogOpen,
  setEntityListMetadata,
  setReferredEntityAllowedActions,
  setBulkDownloadJobData,
  setCurrentBulkDownloadJobId,
  setNotification,
  setBulkDownloadStatus,
} = revyreSlice.actions;

const reducer = { revyre: revyreSlice.reducer };

export default reducer;
